/*
Copyright 2025 TekNix Corporation

This software is proprietary and confidential to TekNix Corporation.
All rights reserved. No part of this software may be reproduced, distributed,
or transmitted in any form or by any means, including photocopying, recording,
or other electronic or mechanical methods, without the prior written permission
of TekNix Corporation, except in the case of brief quotations embodied in
critical reviews and certain other noncommercial uses permitted by copyright law.

For permission requests, write to TekNix Corporation at the address below:
TekNix Corporation
Legal Department

UNAUTHORIZED REPRODUCTION OR DISTRIBUTION IS STRICTLY PROHIBITED.
*/

import type { IMarkerFoundOptions, RoomStateEventHandlerMap } from '../../@types/room-state';
import type { XHubChatClient } from '../../client';
import type { BeaconEventHandlerMap, BeaconIdentifier } from '../beacon/beacon';

import type { XHubChatEvent } from '../event/event';
import { M_BEACON, M_BEACON_INFO } from '../../@types/beacon';
import { EventType, UNSTABLE_MSC2716_MARKER, XHubChatEventEvent } from '../../@types/event';
import { KnownMembership } from '../../@types/membership';
import { RoomStateEvent } from '../../@types/room-state';
import { TypedReEmitter } from '../../emit/ReEmitter';
import { TypedEventEmitter } from '../../emit/typed-event-emitter';
import { removeHiddenChars } from '../../utils';
import { Beacon, BeaconEvent, getBeaconInfoIdentifier } from '../beacon/beacon';
import { RoomMember } from './room-member';

type EmittedRoomStateEvents = RoomStateEvent | BeaconEvent;
type EventHandlerMap = RoomStateEventHandlerMap & BeaconEventHandlerMap;

enum OobStatus {
  NotStarted,
  InProgress,
  Finished,
}

export class RoomState extends TypedEventEmitter<EmittedRoomStateEvents, EventHandlerMap> {
  public readonly reEmitter = new TypedReEmitter<EmittedRoomStateEvents, EventHandlerMap>(this);
  private sentinels: Record<string, RoomMember> = {}; // userId: RoomMember
  private userIdsToDisplayNames: Record<string, string> = {};

  private displayNameToUserIds = new Map<string, string[]>();

  private modified = -1;

  // XXX: Should be read-only
  // The room member dictionary, keyed on the user's ID.
  public members: Record<string, RoomMember> = {}; // userId: RoomMember
  // The state events dictionary, keyed on the event type and then the state_key value.
  public events = new Map<string, Map<string, XHubChatEvent>>(); // Map<eventType, Map<stateKey, XHubChatEvent>>
  // The pagination token for this state.
  public paginationToken: string | null = null;

  public readonly beacons = new Map<BeaconIdentifier, Beacon>();
  private _liveBeaconIds: BeaconIdentifier[] = [];

  /**
   * Construct room state.
   *
   * Room State represents the state of the room at a given point.
   * It can be mutated by adding state events to it.
   * There are two types of room member associated with a state event:
   * normal member objects (accessed via getMember/getMembers) which mutate
   * with the state to represent the current state of that room/user, e.g.
   * the object returned by `getMember('@bob:example.com')` will mutate to
   * get a different display name if Bob later changes his display name
   * in the room.
   * There are also 'sentinel' members (accessed via getSentinelMember).
   * These also represent the state of room members at the point in time
   * represented by the RoomState object, but unlike objects from getMember,
   * sentinel objects will always represent the room state as at the time
   * getSentinelMember was called, so if Bob subsequently changes his display
   * name, a room member object previously acquired with getSentinelMember
   * will still have his old display name. Calling getSentinelMember again
   * after the display name change will return a new RoomMember object
   * with Bob's new display name.
   *
   * @param roomId - Optional. The ID of the room which has this state.
   * If none is specified it just tracks paginationTokens, useful for noti TimelineSet
   * @param oobMemberFlags - Optional. The state of loading out of bound members.
   * As the timeline might get reset while they are loading, this state needs to be inherited
   * and shared when the room state is cloned for the new timeline.
   * This should only be passed from clone.
   */
  public constructor(
    public readonly roomId: string,
    private oobMemberFlags = { status: OobStatus.NotStarted },
  ) {
    super();
    this.updateModifiedTime();
  }

  /**
   * Update the last modified time to the current time.
   */
  private updateModifiedTime(): void {
    this.modified = Date.now();
  }

  /**
   * Set the current typing event for this room.
   * @param event - The typing event
   */
  public setTypingEvent(event: XHubChatEvent): void {
    Object.values(this.members).forEach((member) => {
      member.setTypingEvent(event);
    });
  }

  /**
   * Get state events from the state of the room.
   * @param eventType - The event type of the state event.
   * @returns A list of events
   */
  public getStateEvents(eventType: EventType | string): XHubChatEvent[];
  /**
   * Get state events from the state of the room.
   * @param eventType - The event type of the state event.
   * @param stateKey - The state_key of the state event.
   * @returns A single event (or null if no match found).
   */
  public getStateEvents(eventType: EventType | string, stateKey: string): XHubChatEvent | null;
  public getStateEvents(eventType: EventType | string, stateKey?: string): XHubChatEvent[] | XHubChatEvent | null {
    if (!this.events.has(eventType)) { // no match
      return stateKey === undefined ? [] : null;
    }
    if (stateKey === undefined) { // return all values
      return Array.from(this.events.get(eventType)!.values());
    }
    const event = this.events.get(eventType)!.get(stateKey);
    return event || null;
  }

  /**
   * Get the timestamp when this room state was last updated. This timestamp is
   * updated when this object has received new state events.
   * @returns The timestamp
   */
  public getLastModifiedTime(): number {
    return this.modified;
  }

  private setBeacon(event: XHubChatEvent): void {
    const beaconIdentifier = getBeaconInfoIdentifier(event);

    if (this.beacons.has(beaconIdentifier)) {
      const beacon = this.beacons.get(beaconIdentifier)!;
      return beacon.update(event);
    }

    const beacon = new Beacon(event);

    this.reEmitter.reEmit<BeaconEvent, BeaconEvent>(beacon, [
      BeaconEvent.New,
      BeaconEvent.Update,
      BeaconEvent.Destroy,
      BeaconEvent.LivenessChange,
    ]);

    this.emit(BeaconEvent.New, event, beacon);
    beacon.on(BeaconEvent.LivenessChange, this.onBeaconLivenessChange.bind(this));
    beacon.on(BeaconEvent.Destroy, this.onBeaconLivenessChange.bind(this));

    this.beacons.set(beacon.identifier, beacon);
  }

  /**
   * Check liveness of room beacons
   * emit RoomStateEvent.BeaconLiveness event
   */
  private onBeaconLivenessChange(): void {
    this._liveBeaconIds = Array.from(this.beacons.values())
      .filter(beacon => beacon.isLive)
      .map(beacon => beacon.identifier);

    this.emit(RoomStateEvent.BeaconLiveness, this, this.hasLiveBeacons);
  }

  public get hasLiveBeacons(): boolean {
    return !!this.liveBeaconIds?.length;
  }

  public get liveBeaconIds(): BeaconIdentifier[] {
    return this._liveBeaconIds;
  }

  private getStateEventMatching(event: XHubChatEvent): XHubChatEvent | null {
    return this.events.get(event.getType())?.get(event.getStateKey()!) ?? null;
  }

  private setStateEvent(event: XHubChatEvent): void {
    if (!this.events.has(event.getType())) {
      this.events.set(event.getType(), new Map());
    }
    this.events.get(event.getType())!.set(event.getStateKey()!, event);
  }

  private updateDisplayNameCache(userId: string, displayName: string): void {
    const oldName = this.userIdsToDisplayNames[userId];
    delete this.userIdsToDisplayNames[userId];
    if (oldName) {
      // Remove the old name from the cache.
      // We clobber the user_id > name lookup but the name -> [user_id] lookup
      // means we need to remove that user ID from that array rather than nuking
      // the lot.
      const strippedOldName = removeHiddenChars(oldName);

      const existingUserIds = this.displayNameToUserIds.get(strippedOldName);
      if (existingUserIds) {
        // remove this user ID from this array
        const filteredUserIDs = existingUserIds.filter(id => id !== userId);
        this.displayNameToUserIds.set(strippedOldName, filteredUserIDs);
      }
    }

    this.userIdsToDisplayNames[userId] = displayName;

    const strippedDisplayname = displayName && removeHiddenChars(displayName);
    // an empty stripped displayname (undefined/'') will be set to MX_ID in room-member.js
    if (strippedDisplayname) {
      const arr = this.displayNameToUserIds.get(strippedDisplayname) ?? [];
      arr.push(userId);
      this.displayNameToUserIds.set(strippedDisplayname, arr);
    }
  }

  public async processBeaconEvents(events: XHubChatEvent[], matrixClient: XHubChatClient): Promise<void> {
    if (
      !events.length
      // discard locations if we have no beacons
      || !this.beacons.size
    ) {
      return;
    }

    const beaconByEventIdDict = [...this.beacons.values()].reduce<Record<string, Beacon>>((dict, beacon) => {
      dict[beacon.beaconInfoId] = beacon;
      return dict;
    }, {});

    const processBeaconRelation = (beaconInfoEventId: string, event: XHubChatEvent): void => {
      if (!M_BEACON.matches(event.getType())) {
        return;
      }

      const beacon = beaconByEventIdDict[beaconInfoEventId];

      if (beacon) {
        beacon.addLocations([event]);
      }
    };

    for (const event of events) {
      const relatedToEventId = event.getRelation()?.event_id;
      // not related to a beacon we know about; discard
      if (!relatedToEventId || !beaconByEventIdDict[relatedToEventId]) return;
      if (!M_BEACON.matches(event.getType()) && !event.isEncrypted()) return;

      try {
        await matrixClient.decryptEventIfNeeded(event);
        processBeaconRelation(relatedToEventId, event);
      } catch {
        if (event.isDecryptionFailure()) {
          // add an event listener for once the event is decrypted.
          event.once(XHubChatEventEvent.Decrypted, async () => {
            processBeaconRelation(relatedToEventId, event);
          });
        }
      }
    }
  }

  /**
   * Looks up a member by the given userId, and if it doesn't exist,
   * create it and emit the `RoomState.newMember` event.
   * This method makes sure the member is added to the members dictionary
   * before emitting, as this is done from setStateEvents and setOutOfBandMember.
   * @param userId - the id of the user to look up
   * @param channel_slug - the channel slug of this room member.
   * @param event - the membership event for the (new) member. Used to emit.
   * @returns the member, existing or newly created.
   *
   * @remarks
   * Fires {@link RoomStateEvent.NewMember}
   */
  private getOrCreateMember(userId: string, channel_slug: string, event: XHubChatEvent): RoomMember {
    let member = this.members[userId];
    if (!member) {
      member = new RoomMember(this.roomId, userId, channel_slug);
      member.setMembershipEvent(event);
      member.setDescription(event.getContent().description || '');

      // add member to members before emitting any events,
      // as event handlers often lookup the member
      this.members[userId] = member;
      this.emit(RoomStateEvent.NewMember, event, this, member);
    }
    return member;
  }

  private updateMember(member: RoomMember): void {
    // blow away the sentinel which is now outdated
    delete this.sentinels[member.userId];
    this.members[member.userId] = member;
  }

  /**
   * Get a room member whose properties will not change with this room state. You
   * typically want this if you want to attach a RoomMember to a XHubChatEvent which
   * may no longer be represented correctly by Room.currentState or Room.oldState.
   * The term 'sentinel' refers to the fact that this RoomMember is an unchanging
   * guardian for state at this particular point in time.
   * @param userId - The room member's user ID.
   * @returns The member or null if they do not exist.
   */
  public getSentinelMember(userId: string, channel_slug?: string): RoomMember | null {
    if (!userId) return null;
    let sentinel = this.sentinels[userId];

    if (sentinel === undefined) {
      sentinel = new RoomMember(this.roomId, userId, channel_slug || userId);
      const member = this.members[userId];
      if (member?.events.member) {
        sentinel.setMembershipEvent(member.events.member);
      }
      this.sentinels[userId] = sentinel;
    }
    return sentinel;
  }

  /**
   * Add previously unknown state events.
   * When lazy loading members while back-paginating,
   * the relevant room state for the timeline chunk at the end
   * of the chunk can be set with this method.
   * @param events - state events to prepend
   */
  public setUnknownStateEvents(events: XHubChatEvent[]): void {
    const unknownStateEvents = events.filter((event) => {
      return !this.events.has(event.getType()) || !this.events.get(event.getType())!.has(event.getStateKey()!);
    });

    this.setStateEvents(unknownStateEvents);
  }

  /**
   * Get all RoomMembers in this room.
   * @returns A list of RoomMembers.
   */
  public getMembers(): RoomMember[] {
    return Object.values(this.members);
  }

  /**
   * Get a room member by their user ID.
   * @param userId - The room member's user ID.
   * @returns The member or null if they do not exist.
   */
  public getMember(userId: string): RoomMember | null {
    return this.members[userId] || null;
  }

  /**
   * Creates a copy of this room state so that mutations to either won't affect the other.
   * @returns the copy of the room state
   */
  public clone(): RoomState {
    const copy = new RoomState(this.roomId, this.oobMemberFlags);

    // Ugly hack: because setStateEvents will mark
    // members as superseding future out of bound members
    // if loading is in progress (through oobMemberFlags)
    // since these are not new members, we're merely copying them
    // set the status to not started
    // after copying, we set back the status
    const status = this.oobMemberFlags.status;
    this.oobMemberFlags.status = OobStatus.NotStarted;

    Array.from(this.events.values()).forEach((eventsByStateKey) => {
      copy.setStateEvents(Array.from(eventsByStateKey.values()));
    });

    // Ugly hack: see above
    this.oobMemberFlags.status = status;

    // copy out of band flags if needed
    if (this.oobMemberFlags.status === OobStatus.Finished) {
      // copy markOutOfBand flags
      this.getMembers().forEach((member) => {
        if (member.isOutOfBand()) {
          copy.getMember(member.userId)?.markOutOfBand();
        }
      });
    }

    return copy;
  }

  /**
   * Add an array of one or more state XHubChatEvents, overwriting any existing
   * state with the same `{type, stateKey}` tuple. Will fire "RoomState.events"
   * for every event added. May fire "RoomState.members" if there are
   * `m.room.member` events. May fire "RoomStateEvent.Marker" if there are
   * `UNSTABLE_MSC2716_MARKER` events.
   * @param stateEvents - a list of state events for this room.
   *
   * @remarks
   * Fires {@link RoomStateEvent.Members}
   * Fires {@link RoomStateEvent.NewMember}
   * Fires {@link RoomStateEvent.Events}
   * Fires {@link RoomStateEvent.Marker}
   */
  public setStateEvents(stateEvents: XHubChatEvent[], markerFoundOptions?: IMarkerFoundOptions): void {
    this.updateModifiedTime();

    // update the core event dict
    stateEvents.forEach((event) => {
      if (event.getRoomId() !== this.roomId || !event.isState()) return;

      if (M_BEACON_INFO.matches(event.getType())) this.setBeacon(event);

      const lastStateEvent = this.getStateEventMatching(event);
      this.setStateEvent(event);
      if (event.getType() === EventType.RoomMember) {
        this.updateDisplayNameCache(event.getStateKey()!, event.getContent().displayname ?? '');
      }
      this.emit(RoomStateEvent.Events, event, this, lastStateEvent);
    });

    this.onBeaconLivenessChange();
    // update higher level data structures. This needs to be done AFTER the
    // core event dict as these structures may depend on other state events in
    // the given array (e.g. disambiguating display names in one go to do both
    // clashing names rather than progressively which only catches 1 of them).
    stateEvents.forEach((event) => {
      if (event.getRoomId() !== this.roomId || !event.isState()) return;

      if (event.getType() === EventType.RoomMember) {
        const channel_slug = event.getStateKey()!;

        // leave events apparently elide the displayname or avatar_url,
        // so let's fake one up so that we don't leak user ids
        // into the timeline
        if (event.getContent().membership === KnownMembership.Leave
          || event.getContent().membership === KnownMembership.Ban
        ) {
          event.getContent().avatar_url = event.getContent().avatar_url || event.getPrevContent().avatar_url;
          event.getContent().displayname = event.getContent().displayname || event.getPrevContent().displayname;
        }

        const member = this.getOrCreateMember(channel_slug, channel_slug, event);
        member.setMembershipEvent(event);
        member.setDescription(event.getContent().description || '');

        this.updateMember(member);
        this.emit(RoomStateEvent.Members, event, this, member);
      } else if (UNSTABLE_MSC2716_MARKER.matches(event.getType())) {
        this.emit(RoomStateEvent.Marker, event, markerFoundOptions);
      }
    });

    this.emit(RoomStateEvent.Update, this);
  }
}
